import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Drawer,
    Form,
    Input,
    message,
    Select,
    Switch,
    InputNumber,
    Divider,
    Space,
    Card,
    Upload
} from "antd";
import service from "../../api/FetchInterceptor";

import { UploadOutlined,  } from "@ant-design/icons";

const formDataHeaders = {
    ...service.defaults.headers,
    "Content-Type": "multipart/form-data",
};

const AddService = ({ id = null, open, setOpen, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const [createData, setCreateData] = useState({
        length_sets: {},
        option_types: [],
        defaults: {},
    });

    const isEdit = !!id;

    const loadCreate = () => {
        service({
            method: "GET",
            url: "admin/services/create",
        }).then((response) => {
            const data = response.data ?? response;
            setCreateData(data);
        });
    };

    const loadService = () => {
        if (!id) return;

        service({
            method: "GET",
            url: `admin/services/${id}`,
        }).then((response) => {
            const data = response.data ?? response;

            // Map prices to object { SHORT: 40, ... }
            const lengthPrices = {};
            (data.prices || []).forEach((p) => {
                if (p.length_key) lengthPrices[p.length_key] = Number(p.price);
            });

            // Guess selected length set from lengths count (simple heuristic)
            let length_set = "STANDARD";
            if ((data.lengths || []).length === 2) length_set = "BUILDER";

            form.setFieldsValue({
                group: data.group,
                title: data.title,
                description: data.description,
                duration_min: data.duration_min,

                is_custom_price: data.is_custom_price,
                has_length: data.has_length,
                has_3d_removal_choice: data.has_3d_removal_choice,
                has_outside_set_choice: data.has_outside_set_choice,

                base_price: Number(data.base_price ?? 0),
                with_3d_removal_price: Number(data.with_3d_removal_price ?? 0),
                outside_set_price: Number(data.outside_set_price ?? 0),

                image_path: data.image_path ?? "",

                length_set,
                length_prices: lengthPrices,

                options:
                    (data.options || []).map((o) => ({
                        label: o.label,
                        type: o.type,
                        add_price: o.add_price !== null ? Number(o.add_price) : null,
                        min_price: o.min_price !== null ? Number(o.min_price) : null,
                        max_price: o.max_price !== null ? Number(o.max_price) : null,
                        add_duration_min: Number(o.add_duration_min ?? 0),
                        is_active: !!o.is_active,
                        sort_order: Number(o.sort_order ?? 1),
                    })) || [],
            });
        });
    };

    useEffect(() => {
        if (open) {
            loadCreate();

            if (isEdit) {
                loadService();
            } else {
                form.resetFields();

                // Apply defaults from create endpoint
                const d = createData.defaults || {};
                form.setFieldsValue({
                    group: "",
                    title: "",
                    description: "",
                    duration_min: d.duration_min ?? 90,

                    is_custom_price: d.is_custom_price ?? false,
                    has_length: d.has_length ?? true,
                    has_3d_removal_choice: d.has_3d_removal_choice ?? false,
                    has_outside_set_choice: d.has_outside_set_choice ?? false,

                    base_price: 0,
                    with_3d_removal_price: 0,
                    outside_set_price: 0,

                    image_path: "",
                    length_set: "STANDARD",
                    length_prices: {},
                    options: [],
                });
            }
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, id]);

    // Use watch to update UI dynamically
    const hasLength = Form.useWatch("has_length", form);
    const lengthSet = Form.useWatch("length_set", form);

    const selectedLengths = useMemo(() => {
        const sets = createData.length_sets || {};
        return sets[lengthSet] || [];
    }, [createData.length_sets, lengthSet]);

    const onSubmit = (values) => {
        setLoading(true);

        // Build payload for backend
        const payload = {
            group: values.group,
            title: values.title,
            description: values.description,
            duration_min: values.duration_min,

            is_custom_price: !!values.is_custom_price,
            has_length: !!values.has_length,
            has_3d_removal_choice: !!values.has_3d_removal_choice,
            has_outside_set_choice: !!values.has_outside_set_choice,

            base_price: Number(values.base_price ?? 0),
            with_3d_removal_price: Number(values.with_3d_removal_price ?? 0),
            outside_set_price: Number(values.outside_set_price ?? 0),

            image_path: values.image_path || null,

            // options are sent from frontend (NOT predefined)
            options: (values.options || []).map((o, idx) => ({
                label: o.label,
                type: o.type,
                add_price: o.type === "FIXED_ADD" ? Number(o.add_price ?? 0) : null,
                min_price: o.type === "VARIABLE_RANGE" ? Number(o.min_price ?? 0) : null,
                max_price: o.type === "VARIABLE_RANGE" ? Number(o.max_price ?? 0) : null,
                add_duration_min: Number(o.add_duration_min ?? 0),
                is_active: o.is_active !== false,
                sort_order: Number(o.sort_order ?? idx + 1),
            })),
        };

        payload.image = values.image[0].originFileObj

        // lengths/prices only if has_length=true
        if (payload.has_length) {
            payload.lengths = selectedLengths.map((l) => ({
                key: l.key,
                label: l.label,
                sort_order: l.sort_order,
            }));

            const lp = values.length_prices || {};
            payload.prices = selectedLengths
                .filter((l) => lp[l.key] !== undefined && lp[l.key] !== null && lp[l.key] !== "")
                .map((l) => ({
                    length_key: l.key,
                    price: Number(lp[l.key]),
                }));

            // If base_price not explicitly set, derive from SHORT if exists
            if (!payload.base_price && lp.SHORT) payload.base_price = Number(lp.SHORT);
        } else {
            payload.lengths = [];
            payload.prices = [];
        }

        if (isEdit) payload._method = "PUT";

        service({
            method: "POST",
            url: isEdit ? `admin/services/${id}` : "admin/services",
            data: payload,
            headers: formDataHeaders,
        })
            .then((response) => {
                const data = response.data ?? response;
                message.success({ content: data.message ?? "Enregistré", duration: 2 });

                form.resetFields();
                setOpen(false);
                onSuccess?.();
            })
            .catch((e) => {
                message.error("Erreur lors de l'enregistrement");
            })
            .finally(() => setLoading(false));
    };

    return (
        <Drawer
            title={isEdit ? "Modifier un service" : "Ajouter un service"}
            open={open}
            onClose={() => setOpen(false)}
            width={800}
        >
            <Form form={form} onFinish={onSubmit} layout="vertical">
                <Form.Item name="group" label="Groupe" rules={[{ required: true }]}>
                    <Input placeholder="Ex: Gel-X, Acrylique, Builder Gel..." />
                </Form.Item>

                <Form.Item name="title" label="Titre" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>

                <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item name="duration_min" label="Durée (minutes)" rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                    name="image"
                    label="Image du service"
                    rules={[{ required: !isEdit, message: "Veuillez uploader une image" }]}
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                >
                    <Upload
                        accept="image/*"
                        listType="picture"
                        maxCount={1}
                        beforeUpload={() => false}
                    >
                        <Button icon={<UploadOutlined />}>Sélectionner une image</Button>
                    </Upload>
                </Form.Item>


                <Divider />

                <Space size="large" wrap>
                    <Form.Item name="has_length" label="Prix par longueur" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="is_custom_price" label="Prix à définir (custom)" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="has_3d_removal_choice" label="Option dépose 3D" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="has_outside_set_choice" label="Option pose extérieure" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Space>

                <Form.Item name="base_price" label="Prix de base">
                    <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>

                <Divider />

                {hasLength ? (
                    <>
                        <Form.Item name="length_set" label="Jeu de longueurs" rules={[{ required: true }]}>
                            <Select
                                options={[
                                    { value: "STANDARD", label: "STANDARD (SHORT/MEDIUM/XL/XXL)" },
                                    { value: "BUILDER", label: "BUILDER (SHORT/MEDIUM)" },
                                ]}
                            />
                        </Form.Item>

                        <Card title="Prix par longueur" size="small">
                            {selectedLengths.map((l) => (
                                <Form.Item
                                    key={l.key}
                                    label={`${l.label} (${l.key})`}
                                    name={["length_prices", l.key]}
                                    rules={[{ required: l.key === "SHORT" }]}
                                >
                                    <InputNumber min={0} style={{ width: "100%" }} />
                                </Form.Item>
                            ))}
                        </Card>

                        <Divider />
                    </>
                ) : null}

                <Form.List name="options">
                    {(fields, { add, remove }) => (
                        <>
                            <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                <h3 style={{ margin: 0 }}>Options (envoyées à la création)</h3>
                                <Button onClick={() => add({ type: "FIXED_ADD", is_active: true, add_duration_min: 0 })}>
                                    + Ajouter une option
                                </Button>
                            </Space>

                            {fields.map((field) => (
                                <Card
                                    key={field.key}
                                    style={{ marginTop: 12 }}
                                    size="small"
                                    title={`Option #${field.name + 1}`}
                                    extra={
                                        <Button danger onClick={() => remove(field.name)}>
                                            Supprimer
                                        </Button>
                                    }
                                >
                                    <Form.Item
                                        {...field}
                                        name={[field.name, "label"]}
                                        label="Libellé"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        {...field}
                                        name={[field.name, "type"]}
                                        label="Type"
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            options={(createData.option_types || []).map((t) => ({
                                                value: t.value,
                                                label: t.label,
                                            }))}
                                        />
                                    </Form.Item>

                                    {/* ✅ Zone reactive au changement du type */}
                                    <Form.Item
                                        noStyle
                                        dependencies={[["options", field.name, "type"]]}
                                    >
                                        {() => {
                                            const type = form.getFieldValue(["options", field.name, "type"]);

                                            return (
                                                <>
                                                    {type === "FIXED_ADD" ? (
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, "add_price"]}
                                                            label="Prix ajouté (+X)"
                                                            rules={[{ required: true }]}
                                                        >
                                                            <InputNumber min={0} style={{ width: "100%" }} />
                                                        </Form.Item>
                                                    ) : null}

                                                    {type === "VARIABLE_RANGE" ? (
                                                        <Space style={{ width: "100%" }} size="middle">
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "min_price"]}
                                                                label="Min"
                                                                rules={[{ required: true }]}
                                                                style={{ flex: 1 }}
                                                            >
                                                                <InputNumber min={0} style={{ width: "100%" }} />
                                                            </Form.Item>

                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "max_price"]}
                                                                label="Max"
                                                                rules={[{ required: true }]}
                                                                style={{ flex: 1 }}
                                                            >
                                                                <InputNumber min={0} style={{ width: "100%" }} />
                                                            </Form.Item>
                                                        </Space>
                                                    ) : null}
                                                </>
                                            );
                                        }}
                                    </Form.Item>

                                    <Form.Item
                                        {...field}
                                        name={[field.name, "add_duration_min"]}
                                        label="Durée ajoutée (min)"
                                    >
                                        <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>

                                    <Space size="large" wrap>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "is_active"]}
                                            label="Active"
                                            valuePropName="checked"
                                            initialValue={true}
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item {...field} name={[field.name, "sort_order"]} label="Ordre">
                                            <InputNumber min={1} />
                                        </Form.Item>
                                    </Space>
                                </Card>
                            ))}
                        </>
                    )}
                </Form.List>

                <Divider />

                <Button loading={loading} type="primary" htmlType="submit">
                    {isEdit ? "Modifier" : "Enregistrer"}
                </Button>
            </Form>
        </Drawer>
    );
};

export default AddService;
